import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faMoneyBillWave, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { Pie, Column, Line } from '@ant-design/plots';
import EscopoBadge from './EscopoBadge';
import InfoTip from './InfoTip';

/**
 * Seção Financeiro do Dashboard Admin (V2).
 * Recebe `data` no shape retornado por GET /api/dashboard-admin/financeiro.
 */

const fmtMoeda = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtData = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '∅';

const Card = ({ titulo, escopo, escopoLabel, info, children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow flex flex-col ${className}`}>
    <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-gray-900 text-sm">{titulo}</span>
      {escopo && <EscopoBadge escopo={escopo} label={escopoLabel} />}
      {info && <InfoTip text={info} position="bottom" />}
    </div>
    <div className="p-4 flex-1 min-h-[260px]">{children}</div>
  </div>
);

const Loading = () => (
  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…
  </div>
);

const Empty = ({ msg = 'Sem dados no período' }) => (
  <div className="flex items-center justify-center h-full text-gray-400 text-sm">{msg}</div>
);

const SecaoFinanceiro = ({ data, loading }) => {
  if (loading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-600" />
          <h2 className="font-semibold text-gray-900">Financeiro</h2>
        </div>
        <div className="bg-white rounded-lg shadow p-12"><Loading /></div>
      </div>
    );
  }
  if (!data) return null;

  // 1. Receita x Despesa 12 meses + projeção 3 meses (linhas duplas)
  const rvdData = data.receita_vs_despesa_12m || [];
  const projData = data.projecao_receita_vs_despesa_3m || [];

  const realRows = rvdData.flatMap(r => [
    { mes: r.mes, valor: r.receita, tipo: 'Receita' },
    { mes: r.mes, valor: r.despesa, tipo: 'Despesa' },
  ]);
  // Ponte: incluir último ponto real como início da projeção para conectar visualmente
  const ult = rvdData[rvdData.length - 1];
  const projRows = (ult && projData.length > 0
    ? [{ mes: ult.mes, valor: ult.receita, tipo: 'Receita (proj.)' },
       { mes: ult.mes, valor: ult.despesa, tipo: 'Despesa (proj.)' },
       ...projData.flatMap(r => [
         { mes: r.mes, valor: r.receita, tipo: 'Receita (proj.)' },
         { mes: r.mes, valor: r.despesa, tipo: 'Despesa (proj.)' },
       ])]
    : []);
  const lineDualData = [...realRows, ...projRows];

  // Eixo Y abreviado (R$ 161 mil em vez de R$ 161.290,40 — economiza
  // espaço sem perder leitura); tooltip mantém o valor cheio.
  const fmtMoedaCurto = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
    if (Math.abs(n) >= 1_000)     return `R$ ${(n / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
    return fmtMoeda(n);
  };
  const lineDualConfig = {
    data: lineDualData,
    xField: 'mes',
    yField: 'valor',
    colorField: 'tipo',
    seriesField: 'tipo',
    smooth: true,
    point: { shapeField: 'circle', sizeField: 3 },
    legend: { position: 'top' },
    scale: { color: { range: ['#16a34a', '#dc2626', '#86efac', '#fca5a5'] } },
    style: { lineDash: (d) => /proj/.test(d.tipo) ? [4, 4] : null },
    height: 260,
    axis: { y: { labelFormatter: (v) => fmtMoedaCurto(v) } },
    tooltip: {
      items: [
        { name: 'Valor', field: 'valor', valueFormatter: (v) => fmtMoeda(v) },
      ],
    },
  };

  // 2. Pie — receita por forma de pagamento
  const fpData = data.receita_por_forma_pagamento?.filter(r => r.valor > 0) || [];
  const fpConfig = {
    data: fpData.map(r => ({ forma: r.forma, valor: r.valor })),
    angleField: 'valor',
    colorField: 'forma',
    innerRadius: 0.5,
    legend: { position: 'bottom' },
    label: { text: 'forma', style: { fontSize: 10 } },
    tooltip: { items: [{ name: 'Valor', field: 'valor', valueFormatter: (v) => fmtMoeda(v) }] },
    height: 240,
  };

  // 3. Top produtos (coluna vertical — converte para Number e ordena).
  const tpData = (data.top_produtos || [])
    .map(p => ({ produto: p.nome, valor: Number(p.valor_total) || 0 }))
    .sort((a, b) => b.valor - a.valor);
  const tpConfig = {
    data: tpData,
    xField: 'produto',
    yField: 'valor',
    height: 280,
    label: { position: 'top', text: (d) => fmtMoeda(d.valor), style: { fontSize: 10 } },
    axis: {
      x: { labelAutoRotate: true, labelFormatter: (v) => v && v.length > 14 ? v.slice(0, 12) + '…' : v },
      y: { labelFormatter: (v) => fmtMoeda(v) },
    },
    tooltip: { items: [{ field: 'valor', name: 'Valor total', valueFormatter: (v) => fmtMoeda(v) }] },
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-600" />
        <h2 className="font-semibold text-gray-900">Financeiro</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card titulo="Receita x Despesa + projeção" escopo="fixo" escopoLabel="últimos 12 meses + 3m" className="lg:col-span-2"
          info="Soma mensal das entradas (Receita, verde) e despesas (Despesa, vermelho) nos últimos 12 meses. As linhas pontilhadas (3 últimos pontos) mostram a projeção para os próximos 3 meses usando média móvel dos 3 últimos meses reais. Despesas não filtram por extensão (não há vínculo na tabela despesa)."
          className="lg:col-span-2">
          {rvdData.length ? <Line {...lineDualConfig} /> : <Empty />}
        </Card>
        <Card titulo="Receita por forma de pagamento" escopo="periodo"
          info="Soma do valor de pedidos PAGOS (pagoEm IS NOT NULL) cadastrados no período, agrupados por forma de pagamento. Filtro de extensão se aplica.">
          {fpData.length ? <Pie {...fpConfig} /> : <Empty msg="Sem pedidos pagos no período" />}
        </Card>
        <Card titulo="Top 10 produtos vendidos" escopo="periodo"
          info="Os 10 produtos com maior soma de valor (em itens de pedido) cadastrados no período. Filtro de extensão se aplica.">
          {tpData.length ? <Column {...tpConfig} /> : <Empty />}
        </Card>
        <Card titulo="Contas a pagar" escopo="fixo" escopoLabel="próximos 30 dias" className="lg:col-span-2"
          info="Contas a pagar com data de vencimento entre hoje e os próximos 30 dias, que ainda não foram pagas. Não considera o filtro de período nem de extensão (contas a pagar não têm vínculo com extensão).">
          {data.contas_a_pagar_proximas?.length ? (
            <table className="min-w-full text-sm">
              <thead className="text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left py-2">Vencimento</th>
                  <th className="text-left py-2">Descrição</th>
                  <th className="text-left py-2">Parcela</th>
                  <th className="text-right py-2">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.contas_a_pagar_proximas.map(c => (
                  <tr key={c.id}>
                    <td className="py-2 text-gray-700">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs mr-1" />
                      {fmtData(c.dataVencimento)}
                    </td>
                    <td className="py-2 text-gray-900">{c.descricao}</td>
                    <td className="py-2 text-gray-600">
                      {c.parcela && c.totalParcelas ? `${c.parcela}/${c.totalParcelas}` : '—'}
                    </td>
                    <td className="py-2 text-right font-medium text-gray-900">{fmtMoeda(c.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty msg="Nenhuma conta a pagar nos próximos 30 dias" />
          )}
        </Card>
      </div>
    </div>
  );
};

export default SecaoFinanceiro;
